import { useEffect, useRef } from 'react'
import { GS1DataMatrixElement } from '@/types'
import { useSettingsStore } from '@/stores/settingsStore'
import { useLabelStore } from '@/stores/labelStore'
import { encodeGS1ForBwipJs } from '@/lib/gs1/gs1Encoder'
import bwipjs from 'bwip-js'

interface GS1DataMatrixElementPreviewProps {
  element: GS1DataMatrixElement
  zoom: number
  mmToPx: number
}

export function GS1DataMatrixElementPreview({ element, zoom, mmToPx }: GS1DataMatrixElementPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { variableData } = useSettingsStore()
  const { label } = useLabelStore()

  // Encode GS1 data
  const gs1Data = encodeGS1ForBwipJs(element.applicationIdentifiers, variableData)

  // DataMatrix size calculation - same formula as regular DataMatrix
  // In ZPL ^BX, the size parameter sets module size in dots
  // A typical DataMatrix has ~14 modules per side for short content
  const dotsPerMm = label.dpi / 25.4
  const estimatedModules = 14
  const sizeInDots = element.size * estimatedModules
  const sizeInMm = sizeInDots / dotsPerMm
  // Calculate scale factor to achieve target size in pixels
  const targetSizePx = Math.max(20, sizeInMm * mmToPx * zoom)
  // bwip-js scale is approximately 2px per module at scale=1
  const scale = Math.max(1, targetSizePx / (estimatedModules * 2))

  useEffect(() => {
    if (!canvasRef.current) return

    const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    // If no AIs defined, show placeholder
    if (!gs1Data || element.applicationIdentifiers.length === 0) {
      canvasRef.current.width = targetSizePx
      canvasRef.current.height = targetSizePx
      ctx.fillStyle = '#f0f0f0'
      ctx.fillRect(0, 0, targetSizePx, targetSizePx)
      ctx.strokeStyle = '#ccc'
      ctx.strokeRect(0, 0, targetSizePx, targetSizePx)
      ctx.fillStyle = '#666'
      ctx.font = `${10 * zoom}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('GS1', targetSizePx / 2, targetSizePx / 2)
      return
    }

    try {
      // Use regular datamatrix with parsefnc to handle FNC1 (^) characters
      // The gs1Data format is: ^FNC10107891234567890172512311^FNC1ABC123
      // where ^FNC1 represents FNC1
      bwipjs.toCanvas(canvasRef.current, {
        bcid: 'datamatrix',
        text: gs1Data,
        scale: scale,
        includetext: false,
        parsefnc: true, // Parse ^FNC1 as FNC1
      })
    } catch (e) {
      console.error('GS1 DataMatrix error:', e, 'Data:', gs1Data)
      // Invalid GS1 data - show error placeholder
      canvasRef.current.width = targetSizePx
      canvasRef.current.height = targetSizePx
      ctx.fillStyle = '#fee2e2'
      ctx.fillRect(0, 0, targetSizePx, targetSizePx)
      ctx.strokeStyle = '#ef4444'
      ctx.lineWidth = 2
      ctx.strokeRect(1, 1, targetSizePx - 2, targetSizePx - 2)
      ctx.fillStyle = '#dc2626'
      ctx.font = `${Math.max(9, 9 * zoom)}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('GS1', targetSizePx / 2, targetSizePx / 2)
    }
  }, [gs1Data, scale, targetSizePx, zoom, element.applicationIdentifiers.length])

  return (
    <div style={{ minWidth: targetSizePx, minHeight: targetSizePx }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block' }}
      />
    </div>
  )
}
